import React, { useState } from 'react';
import { X } from 'lucide-react';

const EditProjectModal = ({ project, onClose, onSave, teamMembers = [] }) => {
  const [showAgentPopup, setShowAgentPopup] = useState(false);
  const [formData, setFormData] = useState({
    clientName: project?.clientName || '',
    primaryAgent: project?.primaryAgent || '',
    propertyAddress: project?.propertyAddress || '',
    type: project?.type || 'Seller',
    ucDate: project?.ucDate || '',
    listPrice: project?.listPrice || '',
    contractPrice: project?.contractPrice || '',
    commissionPercent: project?.commissionPercent || '',
    brokerSplitType: project?.brokerSplitType || 'percent',
    phone: project?.phone || '',
    email: project?.email || '',
    brokerSplitValue: project?.brokerSplitValue || '',
    transactionFee: project?.transactionFee || '',
    tcFee: project?.tcFee || '',
    listingDate: project?.listingDate || '',
    closingDate: project?.closingDate || '',
    opposingAgentName: project?.opposingAgentName || '',
    opposingAgentPhone: project?.opposingAgentPhone || '',
    opposingAgentEmail: project?.opposingAgentEmail || '',
  });

  const handleSubmit = () => {
    const isBuyer = formData.type === 'Buyer';
    if (formData.clientName && (isBuyer || formData.propertyAddress)) {
      const ucDateAdded = formData.ucDate && !project?.ucDate;
      const noAgentYet = !formData.opposingAgentName;
      if (ucDateAdded && noAgentYet) {
        setShowAgentPopup(true);
        return;
      }
      onSave(formData);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) handleSubmit();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-h-screen overflow-auto" onKeyDown={handleKeyDown}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Edit Deal</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2">Deal Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({...formData, type: e.target.value})}
              className="w-full p-3 border rounded"
            >
              <option>Seller</option>
              <option>Buyer</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Primary Agent</label>
            <select
              value={formData.primaryAgent}
              onChange={(e) => setFormData({...formData, primaryAgent: e.target.value})}
              className="w-full p-3 border rounded"
            >
              <option value="">Select agent...</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={`${m.firstName} ${m.lastName}`}>
                  {m.firstName} {m.lastName}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Who is the lead agent on this deal?</p>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Client Name(s)</label>
            <input
              type="text"
              value={formData.clientName}
              onChange={(e) => setFormData({...formData, clientName: e.target.value})}
              className="w-full p-3 border rounded"
              placeholder="John and Jane Doe" 
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Phone Number(s)</label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full p-3 border rounded"
              placeholder="5551234, 5559876"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Email Address(es)</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full p-3 border rounded"
              placeholder="client@email.com"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Property Address</label>
            <input
              type="text"
              value={formData.propertyAddress}
              onChange={(e) => setFormData({...formData, propertyAddress: e.target.value})}
              className="w-full p-3 border rounded"
              placeholder="123 Main St, Jacksonville, FL"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Listing Date</label>
            <input
              type="date"
              value={formData.listingDate}
              onChange={(e) => setFormData({...formData, listingDate: e.target.value})}
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Under Contract Date</label>
            <input
              type="date"
              value={formData.ucDate}
              onChange={(e) => setFormData({...formData, ucDate: e.target.value})}
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Closing Date</label>
            <input
              type="date"
              value={formData.closingDate}
              onChange={(e) => setFormData({...formData, closingDate: e.target.value})}
              className="w-full p-2 border rounded"
            />
          </div>
          
          {showAgentPopup && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
              <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 32, width: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#071D39', marginBottom: 6 }}>
                  {formData.type === 'Seller' ? "Who is the Buyer's Agent? 🤝" : "Who is the Seller's Agent? 🤝"}
                </div>
                <div style={{ fontSize: 13, color: '#89A8B1', marginBottom: 24 }}>
                  Add their contact info for easy reference — you can always update it later.
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#516469', marginBottom: 6, display: 'block' }}>Agent Name</label>
                  <input type="text" value={formData.opposingAgentName}
                    onChange={e => setFormData(prev => ({ ...prev, opposingAgentName: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d0d4da', borderRadius: 8, boxSizing: 'border-box' }}
                    placeholder="Jane Smith" />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#516469', marginBottom: 6, display: 'block' }}>Agent Phone</label>
                  <input type="text" value={formData.opposingAgentPhone}
                    onChange={e => setFormData(prev => ({ ...prev, opposingAgentPhone: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d0d4da', borderRadius: 8, boxSizing: 'border-box' }}
                    placeholder="5551234567" />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#516469', marginBottom: 6, display: 'block' }}>Agent Email</label>
                  <input type="email" value={formData.opposingAgentEmail}
                    onChange={e => setFormData(prev => ({ ...prev, opposingAgentEmail: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d0d4da', borderRadius: 8, boxSizing: 'border-box' }}
                    placeholder="agent@email.com" />
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button onClick={() => { setShowAgentPopup(false); onSave(formData); }}
                    style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '2px solid #89A8B1', color: '#516469', fontWeight: 600, fontSize: 14, cursor: 'pointer', backgroundColor: '#fff', fontFamily: 'inherit' }}>
                    Skip for now
                  </button>
                  <button onClick={() => { setShowAgentPopup(false); onSave(formData); }}
                    style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', backgroundColor: '#75BB2E', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="col-span-2 border-t pt-4 mt-2">
            <h3 className="font-semibold mb-3 tracking-wide" style={{ color: '#071D39' }}>
              {formData.type === 'Seller' ? "BUYER'S AGENT" : "SELLER'S AGENT"}
            </h3>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Agent Name</label>
            <input
              type="text"
              value={formData.opposingAgentName}
              onChange={(e) => setFormData({...formData, opposingAgentName: e.target.value})}
              className="w-full p-3 border rounded"
              placeholder="Jane Smith"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Agent Phone</label>
            <input
              type="text"
              value={formData.opposingAgentPhone}
              onChange={(e) => setFormData({...formData, opposingAgentPhone: e.target.value})}
              className="w-full p-3 border rounded"
              placeholder="5551234567"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Agent Email</label>
            <input
              type="email"
              value={formData.opposingAgentEmail}
              onChange={(e) => setFormData({...formData, opposingAgentEmail: e.target.value})}
              className="w-full p-3 border rounded"
              placeholder="agent@email.com"
            />
          </div>

          <div className="col-span-2 border-t pt-4 mt-2">
            <h3 className="font-semibold mb-3 tracking-wide" style={{ color: '#071D39' }}>DEAL FINANCIALS</h3>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">List Price ($)</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="number"
                min="0"
                value={formData.listPrice}
                onChange={(e) => setFormData({...formData, listPrice: Math.max(0, e.target.value)})}
                className="w-full p-2 pl-7 border rounded"
                placeholder="0"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Contract Price ($)</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="number"
                min="0"
                value={formData.contractPrice}
                onChange={(e) => setFormData({...formData, contractPrice: Math.max(0, e.target.value)})}
                className="w-full p-2 pl-7 border rounded"
                placeholder="0"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Commission (%)</label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.commissionPercent}
                onChange={(e) => setFormData({...formData, commissionPercent: Math.max(0, Math.min(100, e.target.value))})}
                className="w-full p-2 pr-7 border rounded"
                placeholder="3"
              />
              <span className="absolute right-3 top-2 text-gray-500">%</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Broker Split</label>
            <div className="flex gap-2">
              <select
                value={formData.brokerSplitType}
                onChange={(e) => setFormData({...formData, brokerSplitType: e.target.value})}
                className="p-2 border rounded bg-white"
              >
                <option value="percent">Percent (%)</option>
                <option value="flat">Flat ($)</option>
              </select>
              <div className="relative flex-1">
                {formData.brokerSplitType === 'flat' && (
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                )}
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={formData.brokerSplitValue}
                  onChange={(e) => setFormData({...formData, brokerSplitValue: Math.max(0, e.target.value)})}
                  className={`w-full p-2 border rounded ${formData.brokerSplitType === 'flat' ? 'pl-7' : 'pr-7'}`}
                  placeholder={formData.brokerSplitType === 'percent' ? '30' : '500'}
                />
                {formData.brokerSplitType === 'percent' && (
                  <span className="absolute right-3 top-2 text-gray-500">%</span>
                )}
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Transaction Fee ($)</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="number"
                min="0"
                value={formData.transactionFee}
                onChange={(e) => setFormData({...formData, transactionFee: Math.max(0, e.target.value)})}
                className="w-full p-2 pl-7 border rounded"
                placeholder="0"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">TC Fee ($)</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="number"
                min="0"
                value={formData.tcFee}
                onChange={(e) => setFormData({...formData, tcFee: Math.max(0, e.target.value)})}
                className="w-full p-2 pl-7 border rounded"
                placeholder="0"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded font-semibold border-2"
              style={{ borderColor: '#89A8B1', color: '#516469' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 text-white p-3 rounded font-semibold"
              style={{ backgroundColor: '#75BB2E' }}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProjectModal;
